import { useEffect, useMemo, useRef, useState } from 'react'
import { browserTimezone, updateProject, updateSiteSettings, type SiteSettings } from '../../../lib/api'
import { translateServerError, useI18n } from '../../../lib/i18n'
import { can, type ProjectAccessRole } from '@webbuilder/shared-types'
import { useToast } from '../../Toast'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { useUnsavedGuard } from '../../../lib/unsavedChanges'
import { IconCheck, IconArrowLeft, IconCalendarPlus, IconClock,
  IconShoppingBag, IconUserCircle, IconCloudUpload, IconCurrentLocation, IconDatabase, IconFingerprint, IconGauge, IconHistory, IconKey, IconLayout, IconPhoto, IconShieldCheck, IconShieldExclamation, IconTrash, IconWorld, IconX } from '@tabler/icons-react'
import { VelticaMascot } from '@webbuilder/ui'
import { ModalPortal } from '../../ModalPortal'
import { InfoHint } from '../../SettingsControls'

type FaviconKey = 'faviconLightDataUrl' | 'faviconDarkDataUrl' | 'webclipDataUrl'

type IconState = {
  faviconLightDataUrl: string
  faviconDarkDataUrl: string
  webclipDataUrl: string
  showVelticaBadge: boolean
  /** Lo «por completar» en el sitio publicado: como «próximamente» o oculto. */
  pendingSoon: boolean
  /** «Usar carrito» (ya resuelto por el servidor: sin elegir, sí si vende productos físicos). */
  cart: boolean
  /** Cuentas de clientes en el sitio: entrar con un código y «Mi cuenta». */
  customerAccounts: 'on' | 'off' | 'auto'
}

/**
 * A qué tamaño se guarda cada icono.
 *
 * Se guardaban los tres a 512×512 PNG sin pérdida. Un favicon se pinta a 16–64
 * px y el webclip a 180, así que eso era peso tirado: con una foto de origen
 * cada icono rondaba el medio mega en base64 y los tres viajaban juntos en el
 * mismo PATCH, porque el parche los manda siempre en bloque.
 */
const ICON_OUTPUT_SIZE: Record<FaviconKey, number> = {
  faviconLightDataUrl: 128,
  faviconDarkDataUrl: 128,
  webclipDataUrl: 180,
}

/** Con qué calidad se guarda lo que no necesita transparencia. */
const ICON_JPEG_QUALITY = 0.85

/**
 * Si el lienzo tiene algún píxel translúcido.
 *
 * Es lo que decide el formato, y no el tipo del archivo de origen: un logo con
 * fondo transparente necesita PNG sí o sí, mientras que una foto opaca pesa
 * varias veces menos en JPEG. Mirar el resultado y no la entrada también acierta
 * con una imagen que no sea cuadrada, donde las bandas del encuadre quedan
 * transparentes y obligan a PNG aunque la foto en sí fuera opaca.
 */
function hasTransparency(context: CanvasRenderingContext2D, size: number): boolean {
  const { data } = context.getImageData(0, 0, size, size)
  for (let index = 3; index < data.length; index += 4) {
    if ((data[index] ?? 255) < 255) return true
  }
  return false
}

function iconStateFrom(settings: SiteSettings | undefined): IconState {
  return {
    faviconLightDataUrl: settings?.faviconLightDataUrl ?? '',
    faviconDarkDataUrl: settings?.faviconDarkDataUrl ?? '',
    webclipDataUrl: settings?.webclipDataUrl ?? '',
    showVelticaBadge: settings?.showVelticaBadge !== false,
    pendingSoon: settings?.pendingItems !== 'hide',
    cart: settings?.cart === 'on',
    customerAccounts: settings?.customerAccounts ?? 'off',
  }
}

/** Every zone the runtime knows, so the list never drifts from the server's. */
function allTimezones(): string[] {
  try {
    const zones = Intl.supportedValuesOf('timeZone')
    return zones.includes('UTC') ? zones : ['UTC', ...zones]
  } catch {
    return ['UTC']
  }
}

/**
 * "America/Los_Angeles" → "America / Los Angeles (GMT-7)"
 *
 * El idioma entra por parámetro y ya no está escrito aquí: era `'es'` fijo, así
 * que el panel en inglés habría seguido escribiendo las fechas en español. Es
 * el fallo que más se repite al traducir, porque no salta en ninguna prueba de
 * tipos: `Intl` acepta cualquier cadena.
 */
function timezoneLabel(zone: string, language: string): string {
  const pretty = zone.replace(/_/g, ' ').replace('/', ' / ')
  try {
    const parts = new Intl.DateTimeFormat(language, { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date())
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value
    return offset ? `${pretty} (${offset})` : pretty
  } catch {
    return pretty
  }
}

/* `fmtDateTime` y `relativeLabel` vivían aquí con `'es'` escrito dentro. Ahora
   salen de `useI18n().fmt`, que los construye con el idioma vigente: son los
   mismos formatos para todo el panel y cambian solos al cambiar de idioma. */

function Toggle({ checked, onChange, label, disabled = false }: { checked: boolean; onChange: (value: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      className={`dash-general-toggle${checked ? ' is-on' : ''}`}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  )
}

/** El nombre para comparar al confirmar: sin acentos, mayúsculas ni espacios de más. */
function confirmKey(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim().toLowerCase()
}

export function GeneralSettings({
  projectName,
  projectId,
  token,
  timezone,
  pagesCount,
  collectionItemsCount,
  createdAt,
  updatedAt,
  sitePublishedAt,
  siteSettings,
  onSiteSettingsChange,
  onDeleted,
  projectRole = 'owner',
  onRenamed,
  onTimezoneChange,
  onBack,
}: {
  projectName: string
  projectId?: string
  token?: string
  timezone?: string
  pagesCount: number
  collectionItemsCount: number
  createdAt?: string | undefined
  updatedAt?: string | undefined
  sitePublishedAt?: string | null | undefined
  siteSettings?: SiteSettings | undefined
  onSiteSettingsChange?: ((settings: SiteSettings) => void) | undefined
  onDeleted?: () => void
  /**
   * Qué eres en este proyecto.
   *
   * Hace falta aquí porque esta pantalla la abre cualquiera con
   * `project.settings` —o sea, también un administrador invitado— y contiene
   * las dos cosas que sólo puede hacer quien lo tiene a su nombre. Por defecto
   * `owner` para no esconderlas a quien construya el componente sin pasarlo.
   */
  projectRole?: ProjectAccessRole
  onRenamed?: (name: string) => void
  onTimezoneChange?: (timezone: string) => void
  /** Vuelta al índice de Configuración, de donde se llega. */
  onBack?: (() => void) | undefined
}) {
  const { showToast } = useToast()

  /* Sólo el propietario. `project.danger` no está en ningún rol del catálogo,
     así que `can` sólo la concede a `owner`. */
  const canDanger = can(projectRole, 'project.danger')
  const faviconLightRef = useRef<HTMLInputElement>(null)
  const faviconDarkRef = useRef<HTMLInputElement>(null)
  const webclipRef = useRef<HTMLInputElement>(null)

  const { t, fmt, language } = useI18n()
  const [confirmOpen, setConfirmOpen] = useState(false)
  /* Escribir el nombre para borrar.
     No es burocracia: un botón rojo se pulsa por inercia, y esto obliga a mirar
     QUÉ proyecto se está borrando. Con varios abiertos en pestañas distintas es
     la única barrera que distingue uno de otro. */
  const [deleteConfirmName, setDeleteConfirmName] = useState('')
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [name, setName] = useState(projectName)
  const [zone, setZone] = useState(timezone ?? 'UTC')
  const [savedName, setSavedName] = useState(projectName.trim())
  const [savedZone, setSavedZone] = useState(timezone ?? 'UTC')

  const initialIcons = useMemo(() => iconStateFrom(siteSettings), [siteSettings])
  const [icons, setIcons] = useState<IconState>(initialIcons)
  const [savedIcons, setSavedIcons] = useState<IconState>(initialIcons)

  // Password: `protected` reflects the current toggle; `newPassword` sets a new one.
  const [passwordProtected, setPasswordProtected] = useState(Boolean(siteSettings?.passwordProtected))
  const [hasPassword, setHasPassword] = useState(Boolean(siteSettings?.hasPassword))
  const [newPassword, setNewPassword] = useState('')

  const [saving, setSaving] = useState(false)
  const zones = useMemo(allTimezones, [])
  const detected = useMemo(browserTimezone, [])

  useEffect(() => {
    setName(projectName)
    setSavedName(projectName.trim())
    setZone(timezone ?? 'UTC')
    setSavedZone(timezone ?? 'UTC')
    const nextIcons = iconStateFrom(siteSettings)
    setIcons(nextIcons)
    setSavedIcons(nextIcons)
    setPasswordProtected(Boolean(siteSettings?.passwordProtected))
    setHasPassword(Boolean(siteSettings?.hasPassword))
    setNewPassword('')
  }, [projectId, projectName, timezone, siteSettings])

  const iconsChanged = JSON.stringify(icons) !== JSON.stringify(savedIcons)
  const passwordChanged = passwordProtected !== Boolean(siteSettings?.passwordProtected) || newPassword.trim().length > 0
  const hasChanges = name.trim() !== savedName || zone !== savedZone || iconsChanged || passwordChanged

  /* Volver con cambios sin guardar hace parpadear la barra de arriba en vez de
     perderlos. */
  const blockUnsaved = useUnsavedGuard()

  function handleDiscardGeneral() {
    setName(savedName)
    setZone(savedZone)
    setIcons(savedIcons)
    setPasswordProtected(Boolean(siteSettings?.passwordProtected))
    setHasPassword(Boolean(siteSettings?.hasPassword))
    setNewPassword('')
    showToast(t('common.discarded'))
  }

  function handleIconFile(file: File | undefined, preference: FaviconKey) {
    if (!file) return
    if (!file.type.startsWith('image/')) { showToast(t('common.pickImage'), 'error'); return }
    if (file.size > 2 * 1024 * 1024) { showToast(t('settings.general.icons.tooBig'), 'error'); return }
    const reader = new FileReader()
    reader.onload = () => {
      const source = typeof reader.result === 'string' ? reader.result : ''
      if (!source) { showToast(t('settings.general.icons.unreadable'), 'error'); return }
      const image = new Image()
      image.onload = () => {
        if (!image.naturalWidth || !image.naturalHeight) { showToast(t('settings.general.icons.failed'), 'error'); return }
        const outputSize = ICON_OUTPUT_SIZE[preference]
        const canvas = document.createElement('canvas')
        canvas.width = outputSize
        canvas.height = outputSize
        const context = canvas.getContext('2d')
        if (!context) { showToast(t('settings.general.icons.failed'), 'error'); return }
        /* Bajar de tres mil píxeles a ciento veintiocho de una sola pasada deja
           bordes dentados con el filtro por defecto, y un icono ES el borde. */
        context.imageSmoothingEnabled = true
        context.imageSmoothingQuality = 'high'
        const scale = Math.min(outputSize / image.naturalWidth, outputSize / image.naturalHeight)
        const width = image.naturalWidth * scale
        const height = image.naturalHeight * scale
        context.drawImage(image, (outputSize - width) / 2, (outputSize - height) / 2, width, height)
        const encoded = hasTransparency(context, outputSize)
          ? canvas.toDataURL('image/png')
          : canvas.toDataURL('image/jpeg', ICON_JPEG_QUALITY)
        setIcons((current) => ({ ...current, [preference]: encoded }))
      }
      image.onerror = () => showToast(t('settings.general.icons.failed'), 'error')
      image.src = source
    }
    reader.readAsDataURL(file)
  }

  async function handleSaveGeneral() {
    if (!projectId || !token) return
    const trimmed = name.trim()
    if (!trimmed) { showToast(t('settings.general.error.emptyName'), 'error'); return }
    const trimmedPassword = newPassword.trim()
    if (passwordProtected && !hasPassword && trimmedPassword.length < 6) {
      showToast(t('settings.general.password.tooShort'), 'error')
      return
    }
    if (trimmedPassword.length > 0 && trimmedPassword.length < 6) {
      showToast(t('settings.general.password.tooShort'), 'error')
      return
    }
    setSaving(true)
    try {
      // Name + timezone live on the project row.
      if (trimmed !== savedName || zone !== savedZone) {
        await updateProject(token, projectId, { name: trimmed, timezone: zone })
      }

      // Favicons / badge / password live in the dedicated site_settings column.
      if (iconsChanged || passwordChanged) {
        const patch: Parameters<typeof updateSiteSettings>[2] = {}
        if (iconsChanged) {
          patch.faviconLightDataUrl = icons.faviconLightDataUrl
          patch.faviconDarkDataUrl = icons.faviconDarkDataUrl
          patch.webclipDataUrl = icons.webclipDataUrl
          patch.showVelticaBadge = icons.showVelticaBadge
          patch.pendingItems = icons.pendingSoon ? 'soon' : 'hide'
          /* Sólo si se cambió: mandarlo siempre dejaría fijo el «según lo que
             vendas» de quien nunca lo tocó. */
          if (icons.cart !== savedIcons.cart) patch.cart = icons.cart ? 'on' : 'off'
          if (icons.customerAccounts !== savedIcons.customerAccounts) patch.customerAccounts = icons.customerAccounts
        }
        if (!passwordProtected && Boolean(siteSettings?.passwordProtected)) {
          patch.passwordProtected = false
        } else if (passwordProtected && trimmedPassword.length >= 6) {
          patch.password = trimmedPassword
        }
        const updated = await updateSiteSettings(token, projectId, patch)
        onSiteSettingsChange?.(updated)
        setHasPassword(updated.hasPassword)
        setPasswordProtected(updated.passwordProtected)
      }

      setSavedName(trimmed)
      setSavedZone(zone)
      setSavedIcons(icons)
      setNewPassword('')
      onRenamed?.(trimmed)
      onTimezoneChange?.(zone)
      showToast(t('common.saved'))
    } catch (err) {
      /* El motivo, y no un «no se pudo» a secas. En producción el worker esconde
         el detalle de un 500, así que el estado HTTP es lo único que separa un
         cuerpo demasiado grande de un fallo de la base. El error entero va a la
         consola, que es donde se puede leer sin ocupar la pantalla. */
      console.error('[ajustes generales] no se pudieron guardar', err)
      showToast(translateServerError(err, t('settings.general.error.saveFailed')), 'error')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Confirmar el borrado. El borrado en sí NO se dispara aquí.
   *
   * Quien lo ejecuta es el panel, con su cortina: es él quien conoce la lista de
   * proyectos y el asistente, así que es quien puede decidir a dónde se va
   * después y taparlo mientras pasa. Esta pantalla sólo dice «adelante»; de ahí
   * que ya no espere ninguna respuesta ni pueda fallar.
   */
  function handleDelete() {
    if (!projectId || !token) return
    setConfirmOpen(false)
    onDeleted?.()
  }

  const overview: Array<{ label: string; value: string; detail?: string; icon: React.ReactNode }> = [
    { label: t('settings.general.overview.id'), value: projectId || t('common.none'), icon: <IconFingerprint size={14} aria-hidden="true" /> },
    { label: t('settings.general.overview.created'), value: fmt.dateTime(createdAt), ...(fmt.relative(createdAt) ? { detail: fmt.relative(createdAt)! } : {}), icon: <IconCalendarPlus size={14} aria-hidden="true" /> },
    { label: t('settings.general.overview.updated'), value: fmt.dateTime(updatedAt), ...(fmt.relative(updatedAt) ? { detail: fmt.relative(updatedAt)! } : {}), icon: <IconHistory size={14} aria-hidden="true" /> },
    { label: t('settings.general.overview.published'), value: sitePublishedAt ? fmt.dateTime(sitePublishedAt) : t('settings.general.overview.unpublished'), ...(sitePublishedAt && fmt.relative(sitePublishedAt) ? { detail: fmt.relative(sitePublishedAt)! } : {}), icon: <IconWorld size={14} aria-hidden="true" /> },
    /* Recuentos por el formateador y no por `String()`: en español son «1.234»
       y en inglés «1,234», y ese punto cambia el número que se lee. */
    { label: t('settings.general.overview.pages'), value: fmt.number(pagesCount), icon: <IconLayout size={14} aria-hidden="true" /> },
    { label: t('settings.general.overview.collectionItems'), value: fmt.number(collectionItemsCount), icon: <IconDatabase size={14} aria-hidden="true" /> },
  ]

  /* Sin distinguir mayúsculas, acentos ni espacios de más: la barrera está
     para que se lea el nombre, no para pelearse con el teclado. «Cafe Lucia»
     vale para «Café Lucía». */
  const nameMatches = confirmKey(projectName || '') !== ''
    && confirmKey(deleteConfirmName) === confirmKey(projectName || '')

  return (
    <div className="dash-project-general-layout">
      <header className="dash-project-general-intro">
        {onBack ? (
          <button type="button" className="dash-secondary-btn dash-settings-back" onClick={() => { if (!blockUnsaved()) onBack() }}>
            <IconArrowLeft size={14} /> {t('config.back')}
          </button>
        ) : null}
        <div className="dash-header">
          <h1>{t('settings.general.title')}</h1>
          <p>{t('settings.general.intro')}</p>
        </div>
        <PendingChangesActions
          dirty={hasChanges}
          saving={saving}
          disabled={!projectId || !token}
          onDiscard={handleDiscardGeneral}
          onSave={() => { void handleSaveGeneral() }}
        />
      </header>

      <div className="dash-project-general-content">
        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconLayout size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.identity.title')}</h3>
              <p>{t('settings.general.identity.intro')}</p>
            </div>
          </div>
          <label className="dash-field">
            <span>{t('settings.general.identity.nameLabel')}</span>
            <input type="text" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconClock size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.timezone.title')}</h3>
              <p>{t('settings.general.timezone.intro')}</p>
            </div>
          </div>
          <div className="dash-general-timezone-control">
            <label className="dash-field">
              <span>{t('settings.general.timezone.label')}</span>
              <select value={zone} onChange={(event) => setZone(event.target.value)}>
                {zones.map((item) => <option key={item} value={item}>{timezoneLabel(item, language)}</option>)}
              </select>
            </label>
            <button
              className="dash-secondary-btn dash-general-detect-timezone"
              type="button"
              onClick={() => { setZone(detected); showToast(t('settings.general.timezone.detected', { zone: timezoneLabel(detected, language) })) }}
            >
              <IconCurrentLocation size={15} /> {t('settings.general.timezone.detect')}
            </button>
          </div>
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconPhoto size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.icons.title')}</h3>
              <p>{t('settings.general.icons.intro')}</p>
            </div>
          </div>

          <div className="dash-general-icon-previews">
            {([
              { label: t('settings.general.icons.faviconLight'), variant: 'is-light', preference: 'faviconLightDataUrl', ref: faviconLightRef },
              { label: t('settings.general.icons.faviconDark'), variant: 'is-dark', preference: 'faviconDarkDataUrl', ref: faviconDarkRef },
              { label: t('settings.general.icons.webclip'), variant: 'is-webclip', preference: 'webclipDataUrl', ref: webclipRef },
            ] as const).map(({ label, variant, preference, ref }) => (
              <div className="dash-general-icon-preview" key={preference}>
                <span>{label}</span>
                <button
                  className={variant}
                  type="button"
                  /* Dos claves enteras y no «cambiar/subir» + el nombre pegado: en
                     inglés el orden y el artículo cambian, y una frase cosida a
                     trozos no se puede traducir sin romperla. */
                  aria-label={t(icons[preference] ? 'settings.general.icons.change' : 'settings.general.icons.upload', { icon: label.toLowerCase() })}
                  onClick={() => ref.current?.click()}
                >
                  {icons[preference]
                    ? <img src={icons[preference]} alt="" />
                    : <IconPhoto size={variant === 'is-webclip' ? 24 : 18} />}
                  <i className="dash-general-icon-upload-mark"><IconCloudUpload size={14} /></i>
                </button>
                <input
                  ref={ref}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  hidden
                  onChange={(event) => { handleIconFile(event.target.files?.[0], preference); event.target.value = '' }}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconKey size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.password.title')}</h3>
              <p>{t('settings.general.password.intro')}</p>
            </div>
          </div>
          <div className="dash-general-setting-row">
            <span className="dash-general-setting-copy">
              <strong>{t('settings.general.password.toggle')}</strong>
              <small>{t('settings.general.password.toggleHint')}</small>
            </span>
            <Toggle
              checked={passwordProtected}
              onChange={(value) => { setPasswordProtected(value); if (!value) setNewPassword('') }}
              label={t('settings.general.password.toggle')}
            />
          </div>
          {passwordProtected ? (
            <label className="dash-field dash-general-password-field">
              <span>{t(hasPassword ? 'settings.general.password.new' : 'settings.general.password.current')}</span>
              <input
                type="password"
                minLength={6}
                value={newPassword}
                placeholder={t(hasPassword ? 'settings.general.password.keepHint' : 'settings.general.password.minHint')}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </label>
          ) : null}
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconWorld size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.badge.title')}</h3>
              <p>{t(siteSettings?.canRemoveVelticaBadge
                ? 'settings.general.badge.introCanRemove'
                : 'settings.general.badge.introFree')}</p>
            </div>
          </div>
          <div className="dash-general-brand-preview" aria-label={t('settings.general.badge.previewAria')}>
            <span className="dash-general-brand-preview-label">{t('settings.general.badge.previewLabel')}</span>
            <span className="dash-general-veltica-badge" aria-hidden="true">
              <VelticaMascot brand size={23} className="dash-general-veltica-mascot" />
              <span>{t('settings.general.badge.madeWith')}</span>
              <img src="/veltica.svg" alt="" />
            </span>
          </div>
          <div className="dash-general-setting-row">
            <span className="dash-general-setting-copy">
              <strong>{t('settings.general.badge.toggle')}</strong>
              <small>{t(siteSettings?.canRemoveVelticaBadge
                ? 'settings.general.badge.hintCanRemove'
                : 'settings.general.badge.hintFree')}</small>
            </span>
            <Toggle
              checked={icons.showVelticaBadge}
              onChange={(showVelticaBadge) => setIcons((current) => ({ ...current, showVelticaBadge }))}
              label={t('settings.general.badge.toggleAria')}
              disabled={!siteSettings?.canRemoveVelticaBadge}
            />
          </div>
          {siteSettings && !siteSettings.canRemoveVelticaBadge ? (
            <p className="dash-general-plan-note">
              {t('settings.general.badge.upsell')}{' '}
              <a href="?view=plans">{t('settings.general.badge.upsellLink')}</a>.
            </p>
          ) : null}
        </section>

        {/* Lo que empezó la IA y sigue por completar: si el sitio publicado lo
            enseña como «próximamente» —su tarjeta sin precio, y al pulsarla un
            aviso— o lo esconde hasta completarlo. */}
        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconClock size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.pending.title')}</h3>
              <p>{t('settings.general.pending.intro')}</p>
            </div>
          </div>
          <div className="dash-general-setting-row">
            <span className="dash-general-setting-copy">
              <strong>{t('settings.general.pending.toggle')}</strong>
              <small>{t(icons.pendingSoon ? 'settings.general.pending.hintSoon' : 'settings.general.pending.hintHide')}</small>
            </span>
            <Toggle
              checked={icons.pendingSoon}
              onChange={(pendingSoon) => setIcons((current) => ({ ...current, pendingSoon }))}
              label={t('settings.general.pending.toggle')}
            />
          </div>
        </section>

        {/* El carrito: varias cosas en un solo pago. Sin tocarlo, encendido si
            el sitio vende productos físicos. Se nota al volver a publicar. */}
        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconShoppingBag size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.cart.title')}</h3>
              <p>{t('settings.general.cart.intro')}</p>
            </div>
          </div>
          <div className="dash-general-setting-row">
            <span className="dash-general-setting-copy">
              <strong>{t('settings.general.cart.toggle')}</strong>
              <small>{t(icons.cart ? 'settings.general.cart.hintOn' : 'settings.general.cart.hintOff')}</small>
            </span>
            <Toggle
              checked={icons.cart}
              onChange={(cart) => setIcons((current) => ({ ...current, cart }))}
              label={t('settings.general.cart.toggle')}
            />
          </div>
        </section>

        {/* Cuentas de clientes: entrar con un código por correo y «Mi
            cuenta» en el sitio. Al momento, sin volver a publicar. */}
        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconUserCircle size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.accounts.title')}</h3>
              <p>{t('settings.general.accounts.intro')}</p>
            </div>
          </div>
          {/* Automático (los proyectos nuevos): encendidas en cuanto el sitio
              publica algo que se compre o se reserve. Siempre o nunca, a mano. */}
          <div className="dash-general-setting-row">
            <span className="dash-general-setting-copy">
              <strong>{t('settings.general.accounts.toggle')}</strong>
              <small>{t(`settings.general.accounts.hint.${icons.customerAccounts}`)}</small>
            </span>
            <label className="dash-field dash-general-accounts-mode">
              <span className="dash-sr-only">{t('settings.general.accounts.toggle')}</span>
              <select value={icons.customerAccounts} onChange={(event) => setIcons((current) => ({ ...current, customerAccounts: event.target.value as IconState['customerAccounts'] }))}>
                <option value="auto">{t('settings.general.accounts.mode.auto')}</option>
                <option value="on">{t('settings.general.accounts.mode.on')}</option>
                <option value="off">{t('settings.general.accounts.mode.off')}</option>
              </select>
            </label>
          </div>
        </section>

        <section className="dash-settings-card dash-general-card">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconGauge size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.overview.title')}</h3>
              <p>{t('settings.general.overview.intro')}</p>
            </div>
          </div>
          <dl className="dash-general-overview">
            {overview.map(({ label, value, detail, icon }) => (
              <div key={label}>
                <dt>{icon}<span>{label}</span></dt>
                <dd>
                  <span>{value}</span>
                  {detail ? <small>{detail}</small> : null}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Entregar va ANTES que borrar. Son la misma clase de decisión —dejar
            de tener este proyecto— y quien baja hasta aquí buscando cómo
            deshacerse de un sitio tiene que encontrarse primero con la opción
            que no destruye nada. */}
        {/*
          * Traspasar y borrar se quedan con el propietario, y aquí se ESCONDEN
          * en vez de deshabilitarse.
          *
          * El servidor ya las rechaza —las dos rutas siguen preguntando por el
          * dueño y no por una capacidad—, así que esto no es la reja: es que la
          * pantalla no prometa algo que va a fallar. Un botón visible que
          * devuelve 403 al pulsarlo se lee como un error del producto, y uno
          * gris invita a preguntar por qué no se puede.
          */}
        {canDanger ? (
        <>
        <section className="dash-settings-card dash-general-card dash-general-danger">
          <div className="dash-settings-card-head">
            <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconTrash size={18} /></span>
            <div className="dash-settings-card-head-copy">
              <h3>{t('settings.general.danger.title')}</h3>
              <p>{t('settings.general.danger.intro')}</p>
            </div>
          </div>
          <div className="dash-general-danger-row">
            <span>
              <strong>{t('settings.general.danger.delete')}</strong>
              <small>{t('settings.general.danger.deleteHint')}</small>
            </span>
            <button
              className="dash-danger-btn"
              type="button"
              onClick={() => { setDeleteError(null); setDeleteConfirmName(''); setConfirmOpen(true) }}
              disabled={!projectId || !token}
            >
              <IconTrash size={14} /> {t('settings.general.danger.delete')}
            </button>
          </div>
        </section>
        </>
        ) : null}
      </div>

      {confirmOpen ? (
        <ModalPortal>
        <div className="dash-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setConfirmOpen(false) }}>
          <section className="dash-modal dash-delete-project-modal" role="dialog" aria-modal="true" aria-labelledby="delete-project-title">
            {/* Una frase arriba y el detalle en su «i»: el modal eran cuatro
                tarjetas por lado, un aviso y una nota, y en el teléfono no se
                llegaba al botón. Lo que se borra y lo que se queda sigue dicho
                por su nombre —eso es lo que convierte el aviso en una decisión
                informada—, una línea cada cosa y su explicación al pulsar. */}
            <header className="dash-modal-header">
              <div className="dash-modal-icon dash-modal-icon--danger"><IconShieldExclamation size={20} /></div>
              <div className="dash-delete-project-heading">
                <h2 id="delete-project-title">{t('settings.general.delete.title', { project: projectName || t('settings.general.danger.thisProject') })}</h2>
                <p>{t('settings.general.delete.intro')}</p>
                <span className="dash-delete-grace"><IconHistory size={13} aria-hidden="true" />{t('settings.general.delete.gracePill')}<InfoHint text={t('settings.general.delete.graceBody')} /></span>
              </div>
            </header>

            <div className="dash-modal-body">
              <div className="dash-delete-cols">
                <section className="dash-delete-card is-lost" aria-labelledby="delete-impact-title">
                  <h3 id="delete-impact-title"><span className="dash-delete-card-icon" aria-hidden="true"><IconTrash size={13} /></span>{t('settings.general.delete.lostTitle')}</h3>
                  <ul className="dash-delete-rows">
                    {([
                      ['lostSite', 'lostSiteHint'],
                      ['lostCms', 'lostCmsHint'],
                      ['lostCommerce', 'lostCommerceHint'],
                      ['lostAnalytics', 'lostAnalyticsHint'],
                    ] as const).map(([title, hint]) => (
                      <li key={title} className="is-lost">
                        <span className="dash-delete-row-icon" aria-hidden="true"><IconX size={11} stroke={2.6} /></span>
                        <span className="dash-delete-row-label">{t(`settings.general.delete.${title}`)}</span>
                        <InfoHint text={t(`settings.general.delete.${hint}`)} />
                      </li>
                    ))}
                  </ul>
                </section>
                <section className="dash-delete-card is-kept" aria-labelledby="delete-kept-title">
                  <h3 id="delete-kept-title"><span className="dash-delete-card-icon" aria-hidden="true"><IconShieldCheck size={13} /></span>{t('settings.general.delete.keptTitle')}</h3>
                  <ul className="dash-delete-rows">
                    {([
                      ['keptStripe', 'keptStripeHint'],
                      ['keptDomains', 'keptDomainsHint'],
                      ['keptImages', 'keptImagesHint'],
                      ['keptPlan', 'keptPlanHint'],
                    ] as const).map(([title, hint]) => (
                      <li key={title} className="is-kept">
                        <span className="dash-delete-row-icon" aria-hidden="true"><IconCheck size={11} stroke={2.6} /></span>
                        <span className="dash-delete-row-label">{t(`settings.general.delete.${title}`)}</span>
                        <InfoHint text={t(`settings.general.delete.${hint}`)} />
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <label className="dash-delete-confirm">
                {/* El nombre va en negrita DENTRO de la frase, así que la clave lleva
                    un hueco y el trozo destacado se compone aparte: partir la
                    frase en «Escribe» + nombre + «para confirmar» la haría
                    intraducible, porque en inglés el orden es otro. */}
                <span>{t('settings.general.delete.confirmLabel', { project: '\u0000' })
                  .split('\u0000')
                  .flatMap((chunk, index) => (index === 0
                    ? [chunk]
                    : [<strong key="name">{projectName || t('settings.general.delete.confirmFallback')}</strong>, chunk]))}</span>
                <input
                  type="text"
                  value={deleteConfirmName}
                  autoComplete="off"
                  spellCheck={false}
                  title={t('settings.general.delete.confirmHint')}
                  placeholder={projectName || t('settings.general.delete.confirmPlaceholder')}
                  onChange={(event) => setDeleteConfirmName(event.target.value)}
                />
              </label>

              {deleteError ? <p className="dash-card-description" style={{ margin: 0, color: '#ef4444' }}>{deleteError}</p> : null}
            </div>

            <footer>
              <button className="dash-secondary-btn" type="button" onClick={() => setConfirmOpen(false)}>{t('common.cancel')}</button>
              <button
                className="dash-danger-btn"
                type="button"
                onClick={() => { void handleDelete() }}
                disabled={!nameMatches}
                title={nameMatches ? undefined : t('settings.general.delete.confirmTooltip')}
              >
                <IconTrash size={14} />
                {/* Ya no dice «permanentemente»: no lo es. Hay 30 días para
                    recuperarlo desde el selector de proyectos. */}
                {t('settings.general.danger.delete')}
              </button>
            </footer>
          </section>
        </div>
        </ModalPortal>
      ) : null}
    </div>
  )
}
